import type { AppShellIconKey } from "../layout/appShellIcons";
import { reorderNavigationItems } from "./adminNavigationOrder";
import type { AdminNavigationSettings } from "./adminNavigationSettingsApi";

/** Anzahl der Pfadsegmente (leeres Segment vor dem ersten Schrägstrich plus Wurzelsegment), die die Admin-Wurzel bilden. */
const ROOT_PATH_SEGMENTS = 2;
const adminRootPath = (catalog: AdminNavigationCatalog) => catalog.homePath.split("/").slice(0, ROOT_PATH_SEGMENTS).join("/");

export type AdminNavigationGroupId = "tenants" | "billing" | "domain" | "operations" | "settings";
export interface AdminCatalogItem {
  path: string;
  titleKey: string;
  icon: AppShellIconKey;
  descriptionKey?: string;
  /** Existing alternate routes select the same entry; they are never rendered as duplicates. */
  aliases?: readonly string[];
  /** Settings subsections share a parent page without adding another sidebar level. */
  parentPath?: string;
}
export interface AdminCatalogEntry extends AdminCatalogItem { group: AdminNavigationGroupId }
export interface AdminNavigationCatalog {
  homePath: string;
  entries: readonly AdminCatalogEntry[];
  domain?: { titleKey: string; icon: AppShellIconKey };
  /** Keep established group paths/keys and stored ordering scopes when adopting the catalog. */
  groupPaths?: Partial<Record<AdminNavigationGroupId, string>>;
  groupKeys?: Partial<Record<AdminNavigationGroupId, string>>;
  orderScope?: string;
  groupOrderScopes?: Partial<Record<AdminNavigationGroupId, readonly string[]>>;
  rootOrderScopes?: readonly string[];
}
const commonGroups = [
  { id: "tenants", titleKey: "AdminStructure.tenants", icon: "ApartmentOutlined" },
  { id: "billing", titleKey: "AdminStructure.billing", icon: "CreditCardOutlined" },
  { id: "domain", titleKey: "", icon: "ControlOutlined" },
  { id: "operations", titleKey: "AdminStructure.operations", icon: "ToolOutlined" },
  { id: "settings", titleKey: "AdminStructure.settings", icon: "SettingOutlined" },
] as const;

export function adminNavigationGroups(catalog: AdminNavigationCatalog, settings?: AdminNavigationSettings | null) {
  const root = adminRootPath(catalog);
  const groups = commonGroups.flatMap(group => {
    if (group.id === "domain" && !catalog.domain) return [];
    const items = catalog.entries.filter(entry => entry.group === group.id && !entry.parentPath);
    const scopes = [...(catalog.groupOrderScopes?.[group.id] ?? []), ...(catalog.orderScope ? [catalog.orderScope] : [])];
    const order = scopes.flatMap(scope => settings?.scopes?.[scope]?.order ?? []);
    const ordered = reorderNavigationItems(items.map(item => ({ ...item, key: item.path })), order);
    const path = catalog.groupPaths?.[group.id] ?? (group.id === "settings" ? `${root}/settings` : `${root}/area/${group.id}`);
    return items.length ? [{ id: group.id, key: catalog.groupKeys?.[group.id] ?? path, path,
      titleKey: group.id === "domain" ? catalog.domain!.titleKey : group.titleKey,
      icon: group.id === "domain" ? catalog.domain!.icon : group.icon, items: ordered }] : [];
  });
  return reorderNavigationItems(groups, (catalog.rootOrderScopes ?? []).flatMap(scope => settings?.scopes?.[scope]?.order ?? []));
}

/** Menu, cards, selection and back navigation consume the same product entries. */
export interface AdminCatalogMenuItem { key: string; label: string; icon: AppShellIconKey; path?: string; children?: AdminCatalogMenuItem[] }
export function adminNavigationMenu(catalog: AdminNavigationCatalog, tr: (key: string) => string, settings?: AdminNavigationSettings | null): AdminCatalogMenuItem[] {
  return [
    { key: catalog.homePath, label: tr("AdminStructure.overview"), icon: "DashboardOutlined" as const },
    ...adminNavigationGroups(catalog, settings).map(group => ({ key: group.key, path: group.path, label: tr(group.titleKey), icon: group.icon,
      children: group.items.map(item => ({ key: item.path, label: tr(item.titleKey), icon: item.icon })) })),
  ];
}

export function adminNavigationEntry(catalog: AdminNavigationCatalog, pathname: string) {
  const root = adminRootPath(catalog);
  return catalog.entries.flatMap(entry => [entry.path, ...(entry.aliases ?? [])].map(path => ({ entry, path })))
    .filter(candidate => pathname === candidate.path || (candidate.path !== root && pathname.startsWith(`${candidate.path}/`)))
    .sort((a, b) => b.path.length - a.path.length)[0]?.entry;
}

export function adminNavigationBackTarget(catalog: AdminNavigationCatalog, pathname: string) {
  if (pathname === catalog.homePath) return null;
  const groups = adminNavigationGroups(catalog);
  if (groups.some(group => group.path === pathname)) return { path: catalog.homePath, titleKey: "AdminStructure.overview" };
  const entry = adminNavigationEntry(catalog, pathname);
  // Detail, Neu oder Duplikat unter einem Eintrag führen zur Liste des Eintrags zurück, nicht zu dessen Gruppe.
  if (entry && pathname !== entry.path && !entry.aliases?.includes(pathname)) return { path: entry.path, titleKey: entry.titleKey };
  if (entry?.parentPath) {
    const parent = catalog.entries.find(candidate => candidate.path === entry.parentPath);
    if (parent) return { path: parent.path, titleKey: parent.titleKey };
  }
  const group = groups.find(candidate => candidate.id === entry?.group);
  return group ? { path: group.path, titleKey: group.titleKey } : null;
}

export function adminNavigationSelection(catalog: AdminNavigationCatalog, pathname: string) {
  const entry = adminNavigationEntry(catalog, pathname);
  return entry?.parentPath ?? entry?.path ?? adminNavigationGroups(catalog).find(group => group.path === pathname)?.key ?? pathname;
}
